import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { Search } from "@/shared/ui/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  focusedGroupIdAtom,
  layoutAtom,
  mobileActiveInstanceIdAtom,
  panelPickerOpenAtom,
} from "@/core/state";
import { useIsMobile } from "@/core/hooks";
import { usePermissions } from "@/core/auth";
import { useCapabilities } from "@/apps/useCapabilities";
import {
  AppGlyph,
  Carte,
  Champ,
  Etiquette,
  Grille,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Vide,
} from "@/shared/ui";
import { addPanel, type AddTarget } from "@/shell/layout";
import {
  panelList,
  type PanelDefinition,
  type PanelGroup,
} from "@/shell/PanelRegistry";
import { isPanelVisible } from "./panelVisibility";
import { getPanelVisual } from "./PanelThumbnails";

/**
 * Modale "Add a panel" — picker visuel avec sections par groupe.
 *
 * Visual style (coque, 01-11-2026) :
 *   - `Grille` de `Carte` cliquables par groupe, une étiquette au-dessus
 *   - Chaque card : thumbnail aspect 4/3 + icone + titre + tagline
 *   - Card highlightee (flèches / survol : celle que Entrée ajoutera) = `actif`
 *   - Search bar (`Champ pilule`) masque les sections sans match
 *
 * Trigger :
 *   - Cmd/Ctrl + Shift + P
 *   - "+" dans le header de chaque cockpit-panel
 *
 * Pour ajouter un nouveau panel a un groupe : declarer `group: "Admin"`
 * dans la PanelDefinition + (optionnel) ajouter un thumbnail dans
 * `PanelThumbnails.tsx`. Sans thumbnail, fallback gris.
 */
const GROUP_ORDER: PanelGroup[] = ["Default", "Channels", "Admin"];

export function PanelPicker() {
  const { t } = useTranslation();
  const [open, setOpen] = useAtom(panelPickerOpenAtom);
  const setLayout = useSetAtom(layoutAtom);
  const focusedGroupId = useAtomValue(focusedGroupIdAtom);
  const setMobileActive = useSetAtom(mobileActiveInstanceIdAtom);
  const isMobile = useIsMobile();
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  const { permissions, isLoading: permsLoading } = usePermissions();
  const { capabilities, isLoading: capsLoading } = useCapabilities();

  const flatVisible = useMemo<PanelDefinition[]>(() => {
    // Pendant que les perms se résolvent, on cache tout (préfère bloquer un
    // instant que flasher un panel auquel l'user n'a pas droit). Une fois
    // résolu, `permissions` est un Set (possiblement vide).
    if (permsLoading || !permissions || capsLoading || !capabilities) return [];
    // Le prédicat vit dans `panelVisibility.ts` — il est testé là-bas, parce
    // qu'enfermé ici il a posé la mauvaise question pendant des mois.
    const visible = panelList.filter((p) =>
      isPanelVisible(p, {
        permissions,
        capabilities,
      }),
    );
    if (!query.trim()) return visible;
    const q = query.toLowerCase();
    return visible.filter((p) => {
      const localizedTitle = t(`panels.registry.${p.id}.title`).toLowerCase();
      const localizedDesc = t(`panels.registry.${p.id}.description`).toLowerCase();
      return (
        localizedTitle.includes(q) ||
        localizedDesc.includes(q) ||
        p.title.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        (p.tags?.some((tag) => tag.toLowerCase().includes(q)) ?? false)
      );
    });
  }, [
    query,
    t,
    permissions,
    permsLoading,
    capabilities,
    capsLoading,
  ]);

  const grouped = useMemo<Array<[PanelGroup, PanelDefinition[]]>>(() => {
    const map = new Map<PanelGroup, PanelDefinition[]>();
    for (const p of flatVisible) {
      const g = p.group ?? "Default";
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(p);
    }
    return GROUP_ORDER.filter((g) => map.has(g)).map((g) => [g, map.get(g)!]);
  }, [flatVisible]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setHighlight(0);
      // Pas de focus auto sur mobile : ca pop le clavier immediatement,
      // shrink le viewport et fait jerker la modale. Sur mobile l'user
      // tape la searchbar quand il veut filtrer.
      if (!isMobile) {
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    }
  }, [open, isMobile]);

  useEffect(() => {
    setHighlight(0);
  }, [query]);

  function close() {
    setOpen(false);
  }

  function confirm(panelId?: string) {
    const id = panelId ?? flatVisible[highlight]?.id;
    if (!id) return;
    let newInstanceId: string | null = null;
    setLayout((prev) => {
      const target: AddTarget = focusedGroupId
        ? { kind: "group", groupId: focusedGroupId }
        : { kind: "active-group" };
      try {
        const res = addPanel(prev, id, target);
        newInstanceId = res.instanceId;
        return res.layout;
      } catch {
        const res = addPanel(prev, id, { kind: "active-group" });
        newInstanceId = res.instanceId;
        return res.layout;
      }
    });
    // Auto-focus le nouveau panel sur mobile : sans ça, l'utilisateur reste
    // sur l'onglet précédent et doit chercher le nouveau dans le strip. Sur
    // desktop, addPanel positionne deja activeIndex sur la nouvelle instance
    // dans son group cible — donc rien à faire.
    if (isMobile && newInstanceId) {
      setMobileActive(newInstanceId);
    }
    close();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
    } else if (e.key === "ArrowDown" || e.key === "ArrowRight") {
      e.preventDefault();
      setHighlight((h) =>
        flatVisible.length === 0 ? 0 : Math.min(h + 1, flatVisible.length - 1),
      );
    } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      confirm();
    }
  }

  return (
    <Modal
      isOpen={open}
      onClose={close}
      size="lg"
      ariaLabel={t("shell.panelPicker.ariaLabel")}
    >
      <ModalHeader
        title={t("shell.panelPicker.title")}
        subtitle={t("shell.panelPicker.subtitle")}
        onClose={close}
        closeLabel={t("common.close")}
      />

      <div className="shrink-0 px-4 pb-3">
        <Champ
          ref={inputRef}
          pilule
          icone={<Search className="h-4 w-4" />}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t("shell.panelPicker.searchPlaceholder")}
          aria-label={t("shell.panelPicker.searchAria")}
        />
      </div>

      <ModalBody padding="px-4 pb-4 pt-2">
        {grouped.length === 0 ? (
          <Vide icone={<Search className="h-6 w-6" />} titre={t("shell.panelPicker.noMatch", { query })} />
        ) : (
          <div className="flex flex-col gap-6">
            {grouped.map(([group, panels]) => (
              <section key={group} className="flex flex-col gap-3">
                <Etiquette as="h3">{t(`panels.registry.groups.${group}`)}</Etiquette>
                <Grille min={170} ecart={12}>
                  {panels.map((p) => {
                    const flatIndex = flatVisible.indexOf(p);
                    const isHighlighted = flatIndex === highlight;
                    const visual = getPanelVisual(p.id);
                    const Icon = visual.icon;
                    const title = t(`panels.registry.${p.id}.title`);
                    const description = t(`panels.registry.${p.id}.description`);
                    return (
                      <Carte
                        key={p.id}
                        remplissage="sm"
                        actif={isHighlighted}
                        onClick={() => confirm(p.id)}
                        onMouseEnter={() => setHighlight(flatIndex)}
                        aria-label={t("shell.panelPicker.addAria", { name: title })}
                        className="flex flex-col gap-3"
                      >
                        <div className="aspect-[4/3] w-full">
                          {visual.thumbnail}
                        </div>
                        <div className="flex flex-col gap-1 px-0.5">
                          <div className="flex items-center gap-2">
                            <AppGlyph
                              slug={p.id}
                              imgClassName="h-4 w-4 shrink-0 rounded-[3px] object-cover"
                            >
                              <Icon className="h-4 w-4 shrink-0 text-muted" />
                            </AppGlyph>
                            <span className="truncate text-[14px] font-medium text-fg">
                              {title}
                            </span>
                          </div>
                          <p className="line-clamp-2 text-[12px] leading-snug text-muted">
                            {description}
                          </p>
                        </div>
                      </Carte>
                    );
                  })}
                </Grille>
              </section>
            ))}
          </div>
        )}
      </ModalBody>

      <ModalFooter className="flex-wrap justify-between text-[12px] text-faint">
        <span>{t("shell.panelPicker.footerHint")}</span>
        <span className="hidden italic sm:inline">{t("shell.panelPicker.footerNote")}</span>
      </ModalFooter>
    </Modal>
  );
}
